/**
 * Keeps cart-dependent club pricing honest on the product page.
 *
 * Record B of the club discount ("a Club54 item is in the cart") means the price a visitor is
 * charged depends on cart contents. Liquid renders that server-side, once, so every cart
 * mutation leaves the rendered price potentially wrong.
 *
 * The direction that matters is REMOVAL. Adding a membership and not seeing the club price
 * until reload is a missed sale; removing one and being left with a club price on screen is a
 * page advertising less than checkout will charge. That is the case this file exists for, and
 * it is why the listener is registered for every cart change rather than only for additions.
 *
 * Elements that need it are marked `data-club-refresh` in Liquid — the price block, the price
 * stack in `product-info`, and the add-to-cart / sticky-bar price spans.
 *
 * Re-fetches the section rather than using Prestige's bundled-section mechanism
 * (`cart:prepare-bundled-sections`). Bundled HTML for `cart/change.js` is rendered against
 * whatever URL Shopify resolves for that request, while this markup depends on the *selected
 * variant*. `location.pathname + location.search` carries the `?variant=` the variant picker
 * keeps in the URL via `replaceState`, so the re-render matches what is actually on screen.
 * One extra request per cart mutation, on product pages only.
 */
(() => {
  const SELECTOR = '[data-club-refresh]';

  let inFlight = false;

  /** The section wrapping the marked elements, or null when this page has none. */
  const findHost = () => document.querySelector(SELECTOR)?.closest('.shopify-section') ?? null;

  const sectionIdOf = (host) => host.id.replace(/^shopify-section-/, '');

  async function refresh() {
    // Cart mutations can fire `cart:change` and `cart:refresh` back to back. The second would
    // race the first and could apply older HTML, so one at a time.
    if (inFlight) return;

    const host = findHost();
    if (!host) return;

    inFlight = true;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionIdOf(host));

      const response = await fetch(url.toString());
      if (!response.ok) return;

      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Scope to the host section on both sides, so a quick-buy modal or a product card
      // elsewhere on the page cannot change the count and desynchronise the pairing.
      const next = fresh.querySelectorAll(SELECTOR);
      const current = host.querySelectorAll(SELECTOR);

      // Bail rather than guess. A count mismatch means the two renders disagree about the
      // shape of the page, and pairing by index would put one surface's price into another's.
      if (next.length === 0 || next.length !== current.length) return;

      current.forEach((element, index) => {
        element.innerHTML = next[index].innerHTML;
      });
    } catch {
      // Leave the server-rendered markup alone. It is stale, but it is the state the page was
      // already in — a failed refresh should not blank out a price.
    } finally {
      inFlight = false;
    }
  }

  document.addEventListener('cart:change', refresh);
  document.addEventListener('cart:refresh', refresh);
})();
